import { track } from '@shared/events';
import { useNavigate, useRouter } from '@tanstack/react-router';

import {
	CalendarSync,
	Info,
	LogIn,
	LogOut,
	MonitorDown,
	Settings,
	ShoppingCart,
	UserRoundPlus,
} from 'lucide-react';

import config from '@/config';
import { USER_ROLE } from '@/constants';
import { AuthCognito, LinkItem } from '@shared/config';
import { cognitoHostedUrl } from '@shared/auth';
import { useSettingsNavigation } from '@shared/lib/settings-route';
import { translate, useT } from '@shared/i18n';

import {
	AUTH_KEY,
	IS_SUBSCRIBED_KEY,
	logoutUserJwt,
} from '@shared/auth/authentication.hooks';

import styles from './menu.module.css';

export class MenuButton {
	static SignOut = SignOutButton;
	static AccountSignOut = AccountSignOutButton;
	static Item = ItemButton;
	static SignUp = SignUpButton;
	static SignIn = SignInButton;
	static Subscribe = SubscribeButton;
	static ManageSubscription = ManageSubscriptionButton;
	static Install = InstallButton;
	static Links = Links;
	static About = AboutButton;
	static Settings = SettingsButton;
	static Favorite = FavoriteButton;
}

/**
 * The Sign out row for menus that aren't given an `onSignOut` of their own
 * (the reader, article and profile menus): signs out of whatever this
 * tenant authenticates with. Cognito goes through the hosted UI's logout;
 * `login`/`jwt` clears the direct session and returns to home, which shows
 * the login screen. Other auth types have no sign-out of their own, so the
 * row is hidden -- it used to build a Cognito URL for them too, which sent a
 * `login` reader to `undefined/logout`. Admins go to `/drafts`, as before.
 * Same red, last row as the AppMenu's entry.
 */
function AccountSignOutButton() {
	const router = useRouter();
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';

	if (isAdmin) {
		return (
			<SignOutButton
				onClick={() => {
					router.navigate({ to: '/drafts' });
				}}
			/>
		);
	}

	if (!isAuthenticated) {
		return null;
	}

	if (!config.app.auth.enabled) {
		return null;
	}

	const type = config.app.auth.type;
	if (type === 'cognito') {
		const redirectUrl = cognitoHostedUrl('logout', router.basepath);
		return (
			<SignOutButton
				onClick={() => {
					if (confirm(translate('common.signOutConfirm'))) {
						track('logout', { method: type });
						location.href = redirectUrl;
					}
				}}
			/>
		);
	}

	if (type === 'login' || type === 'jwt') {
		return (
			<SignOutButton
				onClick={() => {
					if (confirm(translate('common.signOutConfirm'))) {
						track('logout', { method: type });
						logoutUserJwt();
					}
				}}
			/>
		);
	}

	return null;
}

/**
 * The AppMenu's Sign out row -- red, and the last row. Auth-agnostic: what
 * signing out means (a confirm dialog, a hosted-UI redirect...) is the
 * caller's `onClick`, so any template can reuse it.
 */
function SignOutButton({ onClick }: { onClick: () => void }) {
	const t = useT();
	return (
		<button type={'button'} className={styles['danger']} onClick={onClick}>
			<span>{t('common.signOut')}</span>
			<span>
				<LogOut />
			</span>
		</button>
	);
}

/**
 * A plain navigation row -- label on the left, icon on the right, like every
 * other row. `active` marks the page or section the reader is on.
 */
function ItemButton({
	label,
	icon,
	active,
	onClick,
}: {
	label: string;
	icon: React.ReactNode;
	active?: boolean;
	onClick: () => void;
}) {
	return (
		<button
			type={'button'}
			className={active ? styles['active'] : undefined}
			aria-current={active ? 'page' : undefined}
			onClick={onClick}
		>
			<span>{label}</span>
			<span>{icon}</span>
		</button>
	);
}

function SignUpButton() {
	const t = useT();
	const router = useRouter();
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';

	if (isAdmin) {
		return null;
	}
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}

	if (!config.app.auth.enabled) {
		return null;
	}

	if (isAuthenticated) {
		return null;
	}

	const redirectUrl = cognitoHostedUrl('signup', router.basepath);

	return (
		<button
			type={'button'}
			onClick={() => {
				location.href = redirectUrl;
			}}
		>
			<span>{t('common.signUp')}</span>
			<span>
				<UserRoundPlus />
			</span>
		</button>
	);
}

function FavoriteButton() {
	const t = useT();
	if (!config.app.favorite) return null;

	return <button>{t('common.favorite')}</button>;
}

function SignInButton() {
	const t = useT();
	const router = useRouter();
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';

	if (isAdmin) {
		return null;
	}
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}

	if (!config.app.auth.enabled) {
		return null;
	}

	if (isAuthenticated) {
		return null;
	}

	const redirectUrl = cognitoHostedUrl('login', router.basepath);

	return (
		<button
			type={'button'}
			onClick={() => {
				location.href = redirectUrl;
			}}
		>
			<span>{t('common.signIn')}</span>
			<span>
				<LogIn />
			</span>
		</button>
	);
}

function SubscribeButton() {
	const t = useT();
	const navigate = useNavigate();
	const router = useRouter();
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	// Only an app that sells subscriptions has the page. A template whose
	// content is free (starmagazine, toyota) has none, and a reader still
	// signed in from before must not be sent to a missing route.
	if (!('/subscriptions' in router.routesByPath)) {
		return null;
	}
	const isSubscribed = localStorage.getItem(IS_SUBSCRIBED_KEY);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	if (isAdmin) {
		return null;
	}
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}

	if (isSubscribed || !isAuthenticated) {
		return null;
	}

	return (
		<button
			type={'button'}
			onClick={() => {
				track('subscribe_click', { location: window.location.pathname });
				navigate({
					to: '/subscriptions',
				});
			}}
		>
			<span>{t('common.subscribe')}</span>
			<span>
				<ShoppingCart />
			</span>
		</button>
	);
}

function ManageSubscriptionButton() {
	const t = useT();
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const isSubscribed = localStorage.getItem(IS_SUBSCRIBED_KEY);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	if (isAdmin) {
		return null;
	}
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}

	const auth = config.app.auth as AuthCognito;

	if (!isSubscribed || !isAuthenticated) {
		return null;
	}

	const { customerPortalUrl } = auth.config;
	if (!customerPortalUrl) {
		return null;
	}

	return (
		<button
			type={'button'}
			onClick={() => {
				const r = open(customerPortalUrl, '_blank');
				if (r !== null) {
					r.focus();
				}
			}}
		>
			<span>{t('common.manageSubscription')}</span>
			<span>
				<CalendarSync />
			</span>
		</button>
	);
}

function InstallButton({
	show,
	onClick,
}: InstallButtonProps): React.ReactElement | null {
	const t = useT();
	if (!show) {
		return null;
	}

	if (!onClick) {
		return null;
	}

	return (
		<button type={'button'} onClick={onClick}>
			<span>{t('common.installApp')}</span>
			<span>
				<MonitorDown />
			</span>
		</button>
	);
}

export interface InstallButtonProps {
	show?: boolean;
	onClick?: () => void;
}

function AboutButton({
	onClick,
}: InstallButtonProps): React.ReactElement | null {
	const t = useT();
	if (!onClick) {
		return null;
	}

	return (
		<button type={'button'} onClick={onClick}>
			<span>{t('common.about')}</span>
			<span>
				<Info />
			</span>
		</button>
	);
}

/**
 * Opens the Settings dialog (`widgets/settings-dialog`) over the current
 * page. Every general menu has it, just above About. `onOpen` runs first,
 * so the menu can close itself rather than stay open under the dialog.
 */
function SettingsButton({ onOpen }: { onOpen?: () => void }) {
	const t = useT();
	const { openSettings } = useSettingsNavigation();
	return (
		<button
			type={'button'}
			onClick={() => {
				onOpen?.();
				openSettings();
			}}
		>
			<span>{t('common.settings')}</span>
			<span>
				<Settings />
			</span>
		</button>
	);
}

export interface AboutButton {
	onClick?: () => void;
}

function Links({ links }: LinkProps) {
	if (!links || !links.length) {
		return null;
	}
	return (
		<section className={styles['links']}>
			{links
				.filter((l) => l.title && l.url)
				.map((l, i) => {
					const { url, title } = l;
					return (
						<a
							key={i}
							href={url}
							target="_blank"
							rel="external nofollow noopener"
						>
							<h4>{title}</h4>
							<div></div>
						</a>
					);
				})}
		</section>
	);
}

interface LinkProps {
	links: Array<LinkItem> | undefined;
}
